import { useEffect, useMemo, useState } from 'react';
import { useWallet } from '../../context/WalletContext';
import {
  calcPerpetualOrderPreview,
  parseQuickOrderConfig,
  validatePerpetualAmount,
} from '../../utils/orderValidation';
import {
  formatStakingLabel,
  listLeverageOptions,
  parsePerpetualPlayConfig,
} from '../../utils/perpetualPlayConfig';
import { formatThumbVolume } from '@formosa/shared/utils/marketThumb';
import { formatPrice } from '../../utils/timedPlayConfig';

function formatLimitPriceInput(price) {
  const value = Number(price);
  if (Number.isNaN(value) || value <= 0) {
    return '';
  }
  return String(value);
}

export default function PerpetualOrderModal({
  open,
  direction,
  product,
  currentPrice,
  priceChange,
  thumb,
  leverage,
  amount,
  openType,
  limitPrice,
  takeProfitEnabled,
  takeProfitPrice,
  stopLossPrice,
  submitting,
  onClose,
  onLeverageChange,
  onAmountChange,
  onOpenTypeChange,
  onLimitPriceChange,
  onTakeProfitEnabledChange,
  onTakeProfitPriceChange,
  onStopLossPriceChange,
  onConfirm,
}) {
  const { balance, refreshWallet } = useWallet();
  const [localError, setLocalError] = useState('');

  const playConfig = useMemo(() => parsePerpetualPlayConfig(product?.playConfig), [product?.playConfig]);
  const leverageOptions = useMemo(() => listLeverageOptions(product?.playConfig), [product?.playConfig]);
  const quickOrder = useMemo(() => parseQuickOrderConfig(product?.playConfig), [product?.playConfig]);

  const preview = calcPerpetualOrderPreview(amount, product?.handlingFeeRate, leverage);
  const validation = useMemo(
    () => validatePerpetualAmount(amount, product, balance, product?.handlingFeeRate, leverage),
    [amount, product, balance, leverage]
  );

  const isMarket = openType !== 'LIMIT';
  const hasAmount = Number(amount) > 0;
  const hasLimitPrice = isMarket || Number(limitPrice) > 0;
  const canConfirm = hasAmount && hasLimitPrice && !submitting;

  useEffect(() => {
    if (!open) {
      return;
    }
    setLocalError('');
    refreshWallet();
  }, [open, refreshWallet]);

  if (!open) {
    return null;
  }

  const isLong = direction === 'LONG';
  const directionLabel = isLong ? '買漲' : '買跌';
  const balanceNum = Number(balance);
  const usableBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;
  const livePriceLabel = formatPrice(currentPrice, 4);
  const changePositive = priceChange?.positive ?? true;
  const changeText = priceChange?.text || '—';
  const pairLabel = product?.name || product?.displayName || product?.marketSymbolCode || '—';
  const highLabel = formatPrice(thumb?.high, 2);
  const lowLabel = formatPrice(thumb?.low, 2);
  const volumeLabel = formatThumbVolume(thumb?.volume);

  function handleOpenTypeChange(nextType) {
    onOpenTypeChange(nextType);
    if (nextType === 'LIMIT' && currentPrice) {
      onLimitPriceChange(formatLimitPriceInput(currentPrice));
    }
  }

  function applyCurrentPrice() {
    if (!currentPrice) {
      return;
    }
    onLimitPriceChange(formatLimitPriceInput(currentPrice));
  }

  function applyPreset(value) {
    setLocalError('');
    onAmountChange(String(value));
  }

  function applyRatio(ratio) {
    if (usableBalance <= 0) {
      setLocalError('USDT 餘額不足');
      return;
    }
    setLocalError('');
    onAmountChange((usableBalance * ratio).toFixed(2));
  }

  function handleConfirmClick() {
    if (!validation.ok) {
      setLocalError(validation.message);
      return;
    }
    if (!isMarket && (!limitPrice || Number(limitPrice) <= 0)) {
      setLocalError('請輸入限價委託價格');
      return;
    }
    setLocalError('');
    onConfirm();
  }

  return (
    <div className="trade-modal">
      <button type="button" className="trade-modal__backdrop" aria-label="關閉" onClick={onClose} />
      <div className="trade-modal__panel perpetual-order-modal">
        <header className="trade-modal__header">
          <h2>永續合約</h2>
          <button type="button" className="trade-modal__close" aria-label="關閉" onClick={onClose}>
            ×
          </button>
        </header>

        <section className="perpetual-order-modal__quote">
          <div className="perpetual-order-modal__symbol">{pairLabel}</div>
          <div className={`perpetual-order-modal__price ${changePositive ? 'is-up' : 'is-down'}`}>
            {livePriceLabel}
          </div>
          <div className={`perpetual-order-modal__change ${changePositive ? 'is-up' : 'is-down'}`}>
            {changeText}
          </div>
          <div className="perpetual-order-modal__stats">
            <div>
              <span>最高</span>
              <strong>{highLabel}</strong>
            </div>
            <div>
              <span>最低</span>
              <strong>{lowLabel}</strong>
            </div>
            <div>
              <span>成交量</span>
              <strong>{volumeLabel}</strong>
            </div>
          </div>
        </section>

        <div className="trade-modal__summary">
          <div className="trade-modal__row">
            <span>方向</span>
            <strong className={isLong ? 'is-up' : 'is-down'}>{directionLabel}</strong>
          </div>
          <div className="trade-modal__row">
            <span>鎖倉期限</span>
            <strong>{formatStakingLabel(playConfig.stakingTimeDays)}</strong>
          </div>
          <div className="trade-modal__row">
            <span>現價</span>
            <strong className={changePositive ? 'is-up' : 'is-down'}>{livePriceLabel}</strong>
          </div>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">開倉類型</div>
          <select
            className="trade-modal__select"
            value={openType}
            onChange={(e) => handleOpenTypeChange(e.target.value)}
          >
            <option value="MARKET">市價開倉</option>
            <option value="LIMIT">現價</option>
          </select>
        </div>

        <div className="trade-modal__section">
          <div className="perpetual-order-modal__price-row">
            <div className="trade-modal__label">開倉價</div>
            {!isMarket && (
              <button type="button" className="perpetual-order-modal__use-price" onClick={applyCurrentPrice}>
                使用現價 {livePriceLabel}
              </button>
            )}
          </div>
          {isMarket ? (
            <input className="trade-modal__input" value={`${livePriceLabel}（市價）`} readOnly disabled />
          ) : (
            <>
              <input
                className="trade-modal__input"
                type="number"
                min="0"
                step="0.0001"
                value={limitPrice}
                placeholder={livePriceLabel}
                onChange={(e) => onLimitPriceChange(e.target.value)}
              />
              <p className="perpetual-order-modal__hint">
                目前市價 <strong className={changePositive ? 'is-up' : 'is-down'}>{livePriceLabel}</strong>
                ，限價單將在價格觸及委託價後成交
              </p>
            </>
          )}
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">槓桿設置</div>
          <select
            className="trade-modal__select"
            value={leverage}
            onChange={(e) => onLeverageChange(e.target.value)}
          >
            {leverageOptions.map((item) => (
              <option key={item} value={item}>{item}X</option>
            ))}
          </select>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">下單金額</div>
          <input
            className="trade-modal__input"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            placeholder={`${product?.minOrderAmount ?? 0} ~ ${product?.maxOrderAmount ?? '—'}`}
          />
          <div className="trade-modal__preset-grid">
            {quickOrder.amountPresets.map((value) => {
              const active = Number(amount) === value;
              return (
                <button
                  key={value}
                  type="button"
                  className={`trade-modal__preset-btn ${active ? 'is-active' : ''}`}
                  onClick={() => applyPreset(value)}
                >
                  {value}
                </button>
              );
            })}
          </div>
          <div className="trade-modal__preset-grid">
            {quickOrder.amountRatios.map((ratio) => (
              <button
                key={ratio}
                type="button"
                className="trade-modal__preset-btn"
                onClick={() => applyRatio(ratio)}
              >
                {Math.round(ratio * 100)}%
              </button>
            ))}
          </div>
        </div>

        <div className="trade-modal__summary">
          <div className="trade-modal__row">
            <span>USDT 餘額</span>
            <strong>{formatPrice(balance, 2)}</strong>
          </div>
          <div className="trade-modal__row">
            <span>保證金</span>
            <strong>{formatPrice(preview.margin, 2)}</strong>
          </div>
          <div className="trade-modal__row">
            <span>手續費</span>
            <strong>{formatPrice(preview.fee, 2)}</strong>
          </div>
        </div>

        <div className="perpetual-order-modal__tpsl">
          <label className="perpetual-order-modal__tpsl-toggle">
            <input
              type="checkbox"
              checked={takeProfitEnabled}
              onChange={(e) => onTakeProfitEnabledChange(e.target.checked)}
            />
            <span>設置止盈止損</span>
          </label>
          {takeProfitEnabled && (
            <>
              <label className="perpetual-order-modal__tpsl-field">
                <span>止盈價</span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={takeProfitPrice}
                  placeholder="設置止盈價格"
                  onChange={(e) => onTakeProfitPriceChange(e.target.value)}
                />
              </label>
              <label className="perpetual-order-modal__tpsl-field">
                <span>止損價</span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={stopLossPrice}
                  placeholder="設置止損價格"
                  onChange={(e) => onStopLossPriceChange(e.target.value)}
                />
              </label>
            </>
          )}
        </div>

        {localError && <p className="trade-modal__error">{localError}</p>}

        <button
          type="button"
          className={`trade-modal__confirm ${canConfirm ? 'is-ready' : ''}`}
          disabled={!canConfirm}
          onClick={handleConfirmClick}
        >
          {submitting ? '提交中…' : '確認下單'}
        </button>
      </div>
    </div>
  );
}
